import React, { useState } from 'react';
import { parseDateOnlyToLocalDate } from '../../shared/lib/date/date-utils';
import styles from './NotificationDropdown.module.css';
import { useIntl } from 'react-intl';

const NotificationDropdown = ({
	notifications,
	onAccept,
	onDecline,
	onMarkAsRead,
	isLoading,
}) => {
	const intl = useIntl();
	const [processingIds, setProcessingIds] = useState(new Set());

	const handleAccept = async notificationId => {
		setProcessingIds(prev => new Set(prev).add(notificationId));
		try {
			await onAccept(notificationId);
		} catch (error) {
			console.error('Failed to accept notification:', error);
		} finally {
			setProcessingIds(prev => {
				const next = new Set(prev);
				next.delete(notificationId);
				return next;
			});
		}
	};

	const handleDecline = async notificationId => {
		setProcessingIds(prev => new Set(prev).add(notificationId));
		try {
			await onDecline(notificationId);
		} catch (error) {
			console.error('Failed to decline notification:', error);
		} finally {
			setProcessingIds(prev => {
				const next = new Set(prev);
				next.delete(notificationId);
				return next;
			});
		}
	};

	const handleMarkAsRead = async notificationId => {
		setProcessingIds(prev => new Set(prev).add(notificationId));
		try {
			await onMarkAsRead(notificationId);
		} catch (error) {
			console.error('Failed to mark notification as read:', error);
		} finally {
			setProcessingIds(prev => {
				const next = new Set(prev);
				next.delete(notificationId);
				return next;
			});
		}
	};

	const formatDate = dateString => {
		if (!dateString) return '';
		try {
			const date = parseDateOnlyToLocalDate(dateString);
			if (!date || isNaN(date.getTime())) return dateString;
			return date.toLocaleDateString('en-US', {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
			});
		} catch (error) {
			return dateString;
		}
	};

	const formatAmount = (amount, currency = 'USD') => {
		if (!amount) return '';
		try {
			const numAmount =
				typeof amount === 'string' ? parseFloat(amount) : amount;
			return new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: currency || 'USD',
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			}).format(numAmount);
		} catch (error) {
			return `${amount} ${currency || 'USD'}`;
		}
	};

	const formatTimeAgo = dateString => {
		if (!dateString) return '';
		try {
			const date = parseDateOnlyToLocalDate(dateString) || new Date(dateString);
			const now = new Date();
			const diffMs = now - date;
			const diffMins = Math.floor(diffMs / 60000);
			const diffHours = Math.floor(diffMs / 3600000);
			const diffDays = Math.floor(diffMs / 86400000);

			if (diffMins < 1)
				return intl.formatMessage({ id: 'NotificationDropdownID18' });
			if (diffMins < 60)
				return `${diffMins} ${intl.formatMessage({ id: 'NotificationDropdownID19' })} `;
			if (diffHours < 24)
				return `${diffHours} ${intl.formatMessage({ id: 'NotificationDropdownID20' })} `;
			if (diffDays < 7)
				return `${diffDays} ${intl.formatMessage({ id: 'NotificationDropdownID21' })} `;
			return formatDate(dateString);
		} catch (error) {
			return '';
		}
	};

	if (notifications.length === 0) {
		return (
			<div className={styles.dropdown}>
				<div className={styles.emptyState}>
					<p> {intl.formatMessage({ id: 'NotificationDropdownID1' })}</p>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.dropdown}>
			<div className={styles.dropdownHeader}>
				<h3>{intl.formatMessage({ id: 'NotificationDropdownID2' })}</h3>
				<span className={styles.count}>{notifications.length}</span>
			</div>
			<div className={styles.notificationsList}>
				{notifications.map(notification => {
					const isProcessing = processingIds.has(notification.id);
					const isRecurring = notification.notification_type === 'recurring';

					return (
						<div key={notification.id} className={styles.notificationItem}>
							<div className={styles.notificationContent}>
								<div className={styles.notificationHeader}>
									<h4 className={styles.notificationTitle}>
										{notification.title}
									</h4>
									<span className={styles.notificationTime}>
										{formatTimeAgo(notification.created_at)}
									</span>
								</div>
								<p className={styles.notificationMessage}>
									{notification.message}
								</p>

								{/* Recurring notification details */}
								{isRecurring && (
									<div className={styles.notificationDetails}>
										{notification.amount && (
											<div className={styles.detailItem}>
												<span className={styles.detailLabel}>
													{intl.formatMessage({
														id: 'NotificationDropdownID3',
													})}
												</span>
												<span className={styles.detailValue}>
													{formatAmount(
														notification.amount,
														notification.currency,
													)}
												</span>
											</div>
										)}
										{notification.occurrence_date && (
											<div className={styles.detailItem}>
												<span className={styles.detailLabel}>
													{intl.formatMessage({
														id: 'NotificationDropdownID4',
													})}
												</span>
												<span className={styles.detailValue}>
													{formatDate(notification.occurrence_date)}
												</span>
											</div>
										)}
										{notification.category_name && (
											<div className={styles.detailItem}>
												<span className={styles.detailLabel}>
													{intl.formatMessage({
														id: 'NotificationDropdownID5',
													})}
												</span>
												<span className={styles.detailValue}>
													{notification.category_name}
												</span>
											</div>
										)}
										{notification.days_before !== null &&
											notification.days_before !== undefined && (
												<div className={styles.detailItem}>
													<span className={styles.detailLabel}>
														{intl.formatMessage({
															id: 'NotificationDropdownID6',
														})}
													</span>
													<span className={styles.detailValue}>
														{notification.days_before === 0
															? intl.formatMessage({
																	id: 'NotificationDropdownID7',
																})
															: `${notification.days_before} ${intl.formatMessage(
																	{
																		id: 'NotificationDropdownID8',
																	},
																)}${
																	notification.days_before !== 1
																		? intl.formatMessage({
																				id: 'NotificationDropdownID9',
																			})
																		: ''
																} ${intl.formatMessage({
																	id: 'NotificationDropdownID10',
																})}`}
													</span>
												</div>
											)}
									</div>
								)}

								{/* Goal notification details */}
								{!isRecurring && (
									<div className={styles.notificationDetails}>
										{notification.goal_title && (
											<div className={styles.detailItem}>
												<span className={styles.detailLabel}>
													{intl.formatMessage({
														id: 'NotificationDropdownID11',
													})}
												</span>
												<span className={styles.detailValue}>
													{notification.goal_title}
												</span>
											</div>
										)}
										{notification.target_amount && (
											<div className={styles.detailItem}>
												<span className={styles.detailLabel}>
													{intl.formatMessage({
														id: 'NotificationDropdownID12',
													})}
												</span>
												<span className={styles.detailValue}>
													{formatAmount(
														notification.target_amount,
														notification.currency,
													)}
												</span>
											</div>
										)}
										{notification.due_date && (
											<div className={styles.detailItem}>
												<span className={styles.detailLabel}>
													{intl.formatMessage({
														id: 'NotificationDropdownID13',
													})}
												</span>
												<span className={styles.detailValue}>
													{formatDate(notification.due_date)}
												</span>
											</div>
										)}
									</div>
								)}
							</div>

							{/* Action buttons */}
							<div className={styles.notificationActions}>
								{isRecurring ? (
									<>
										<button
											className={`${styles.actionButton} ${styles.acceptButton}`}
											onClick={() => handleAccept(notification.id)}
											disabled={isProcessing || isLoading}
										>
											{isProcessing
												? intl.formatMessage({
														id: 'NotificationDropdownID14',
													})
												: intl.formatMessage({
														id: 'NotificationDropdownID15',
													})}
										</button>
										<button
											className={`${styles.actionButton} ${styles.declineButton}`}
											onClick={() => handleDecline(notification.id)}
											disabled={isProcessing || isLoading}
										>
											{isProcessing
												? intl.formatMessage({
														id: 'NotificationDropdownID14',
													})
												: intl.formatMessage({
														id: 'NotificationDropdownID16',
													})}
										</button>
									</>
								) : (
									<button
										className={`${styles.actionButton} ${styles.readButton}`}
										onClick={() => handleMarkAsRead(notification.id)}
										disabled={isProcessing || isLoading}
									>
										{isProcessing
											? intl.formatMessage({
													id: 'NotificationDropdownID14',
												})
											: intl.formatMessage({
													id: 'NotificationDropdownID17',
												})}
									</button>
								)}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default NotificationDropdown;
